import React, { useState, useRef, useLayoutEffect, useEffect } from 'react'
import { AppBar, Box, Stack, Typography, useMediaQuery, Menu, MenuItem } from '@mui/material'
import { useTheme, alpha } from '@mui/system'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useUser } from 'store' 
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'

import { ROUTE_GROUPS } from '../../Sidebar/constants'
import { isPathInRouteGroup } from '../../use-links'
import { AccountsPerimeter } from '../AccountsPerimeter'
import { useBreadcrumbAction } from './BreadcrumbActions'
import HeaderActions from './HeaderActions'

export const Breadcrumbs = ({ children }) => <Stack alignSelf={'center'} width="100%">{children}</Stack>

export const Title = ({ children }) => {
  const theme = useTheme()
  return (
    <Typography component={'span'} sx={{ color: theme.palette.primary.main, fontSize: 20, fontWeight: 300, textTransform: 'capitalize' }}>
      {children}
    </Typography>
  )
}

const LinkItem = ({ label, isActive, onClick, isGhost = false }) => {
  const theme = useTheme()
  return (
    <Stack direction={'row'} alignItems={'center'} sx={{ flexShrink: 0 }}>
      <Stack alignItems={'center'} gap={0} position={'relative'}>
        <Typography
          component={'span'}
          sx={{
            cursor: 'pointer',
            color: isActive ? '#2570EB' : '#656565',
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '30px',
            minHeight: 30,
            px: 1.5,
            borderRadius: '24px',
            backgroundColor: isActive ? '#F2F8FD' : 'transparent',
            whiteSpace: 'nowrap',
            transition: 'color 0.2s ease, background-color 0.2s ease',
            '&:hover': {
              color: isActive ? '#2570EB' : theme.palette.grey[800],
              backgroundColor: '#F7F7F7'
            }
          }}
          onClick={!isGhost ? onClick : undefined}
        >
          {label}
        </Typography>
      </Stack>
    </Stack>
  )
}

export const Links = ({ links, onClick, isWelcomeMessage = false }) => {
  const theme = useTheme()
  const location = useLocation()
  const { t } = useTranslation()
  const [user] = useUser()

  const containerRef = useRef(null)
  const hiddenContainerRef = useRef(null)
  
  const [visibleCount, setVisibleCount] = useState(links.length)
  const [anchorEl, setAnchorEl] = useState(null)
  
  const visibleLinks = links.slice(0, visibleCount)
  const hiddenLinks = links.slice(visibleCount)
  const hasHiddenLinks = hiddenLinks.length > 0
  const isMenuOpen = Boolean(anchorEl) && hasHiddenLinks

  const isLinkActive = (link) => {
    const routeGroup = Object.values(ROUTE_GROUPS).find(group => link.url?.startsWith(group.base))
    return routeGroup ? isPathInRouteGroup(location.pathname, routeGroup) : link.active
  }

  useEffect(() => {
    const handleWindowResize = () => {
      if (anchorEl) {
        setAnchorEl(null)
      }
    }
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [anchorEl])

  useLayoutEffect(() => {
    if (!containerRef.current || !hiddenContainerRef.current) return

    const calculateVisibility = () => {
      const containerWidth = containerRef.current.offsetWidth
      const moreButtonWidth = 40 
      const items = hiddenContainerRef.current.children
      
      let currentWidth = 0
      let count = 0

      for (let i = 0; i < items.length; i++) {
        const itemWidth = items[i].offsetWidth
        const isLastTotal = i === links.length - 1
        const spaceAvailable = isLastTotal ? containerWidth : containerWidth - moreButtonWidth

        if (currentWidth + itemWidth <= spaceAvailable) {
          currentWidth += itemWidth
          count++
        } else {
          break
        }
      }
      setVisibleCount(count)
    }

    const observer = new ResizeObserver(() => {
        calculateVisibility()
    })

    observer.observe(containerRef.current)
    calculateVisibility() // Calcul initial

    return () => observer.disconnect()
  }, [links]) 

  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget)
  const handleCloseMenu = () => setAnchorEl(null)
  const handleMenuClick = (link) => {
    onClick(link)
    handleCloseMenu()
  }

  if (isWelcomeMessage && user) {
    return (
      <Typography component={'span'} sx={{ color: '#292929', fontSize: 12, fontWeight: 500, mt: 0.5, whiteSpace: 'nowrap' }}>
        {t('header.welcome', { firstName: user.first_name, lastName: user.last_name })}
      </Typography>
    )
  }

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
      <Stack
        ref={containerRef}
        direction="row"
        alignItems="center"
        gap={0.25}
        sx={{ width: '100%', height: '30px', overflow: 'hidden' }}
      >
        {visibleLinks.map((link, i) => {
          return (
            <LinkItem
              key={`vis-${i}`}
              label={link.label}
              isActive={isLinkActive(link)}
              onClick={() => onClick(link)}
            />
          )
        })}

        {hasHiddenLinks && (
          <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
            <Box
              onClick={handleOpenMenu}
              sx={{
                cursor: 'pointer',
                color: '#656565',
                display: 'flex',
                alignItems: 'center',
                '&:hover': { color: '#292929' }
              }}
            >
              <MoreHorizIcon fontSize="small" />
            </Box>
          </Stack>
        )}
      </Stack>

      {hasHiddenLinks && (
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleCloseMenu}
          TransitionProps={{ timeout: 0 }} 
          PaperProps={{ 
            sx: { 
              mt: 1.5, 
              maxHeight: 300,
              minWidth: 180,
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              py: 0.5,
              overflow: 'visible',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -6,
                left: 16,
                width: 12,
                height: 12,
                backgroundColor: 'white',
                transform: 'rotate(45deg)',
                borderLeft: '1px solid rgba(0, 0, 0, 0.06)',
                borderTop: '1px solid rgba(0, 0, 0, 0.06)',
              },
            } 
          }}
        >
          {hiddenLinks.map((link, i) => {
            const isActive = isLinkActive(link)
            return (
              <MenuItem 
                key={`menu-${i}`} 
                onClick={() => handleMenuClick(link)} 
                sx={{
                  py: 1,
                  px: 2,
                  mx: 0.5,
                  position: 'relative',
                  borderRadius: '6px',
                  transition: 'all 0.15s ease',
                  backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  '&:focus': {
                    backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  },
                  '&:hover': {
                    backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.12) : 'rgba(0, 0, 0, 0.03)',
                  },
                  '&::before': isActive ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 3,
                    height: '60%',
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: '0 2px 2px 0',
                  } : {},
                }}
              >
                <Typography 
                  sx={{ 
                    fontSize: 12,
                    fontWeight: isActive ? 600 : 500, 
                    color: isActive ? theme.palette.primary.main : '#656565',
                    transition: 'color 0.15s ease',
                  }}
                >
                  {link.label}
                </Typography>
              </MenuItem>
            )
          })}
        </Menu>
      )}

      <Stack
        ref={hiddenContainerRef}
        direction={'row'}
        sx={{ position: 'absolute', top: 0, left: 0, visibility: 'hidden', pointerEvents: 'none', width: 'max-content', minWidth: 'max-content' }}
      >
        {links.map((link, i) => (
          <LinkItem key={`ghost-${i}`} label={link.label} isActive={isLinkActive(link)} onClick={() => {}} isGhost={true} />
        ))}
      </Stack>
    </Box>
  )
}

const HeaderBar = ({ hidePerimeters = false, title, subtitle, titleContent, children, rightContent, fallbackBreadcrumbLabel }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const hasBreadcrumbs = Boolean(children)
  const registeredAction = useBreadcrumbAction()
  const actionContent = rightContent === undefined ? registeredAction : rightContent
  const showBreadcrumbRow = hasBreadcrumbs || Boolean(actionContent)
  const fallbackBreadcrumb = !hasBreadcrumbs && actionContent && fallbackBreadcrumbLabel
    ? (
      <Breadcrumbs>
        <Links
          links={[{ label: fallbackBreadcrumbLabel, active: true }]}
          onClick={() => {}}
        />
      </Breadcrumbs>
      )
    : null

  return (
    <AppBar
      position="relative"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'common.white',
        boxShadow: 'none',
        gap: 0,
        pt: 0,
        pb: showBreadcrumbRow ? '15px' : '22px',
        mb: 0,
        overflow: 'visible'
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'center',
          width: '100%',
          minHeight: 64,
          gap: 2,
          borderBottom: '1px solid #F7F7F7',
          px: 3.5
        }}
      >
        <Stack gap={0.25} minWidth={0} sx={{ overflow: 'hidden' }}>
          {titleContent || (
            <>
              <Title>{title}</Title>
              {subtitle && (
                <Typography
                  component={'span'}
                  sx={{
                    color: '#292929',
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: '16px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </>
          )}
        </Stack>
        <Stack direction="row" gap={1.5} alignItems="center" justifyContent="flex-end" minWidth={0}>
          {!hidePerimeters && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: { xs: 'min(52vw, 500px)', md: '500px' } }}>
              <AccountsPerimeter />
            </Box>
          )}
          <HeaderActions />
        </Stack>
      </Box>

      {!isMobile && showBreadcrumbRow && (
        <Box
          sx={{
            width: '100%',
            px: 3.5,
            pt: '22px'
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: actionContent ? 'minmax(0, 1fr) auto' : 'minmax(0, 1fr)',
              alignItems: 'center',
              width: '100%',
              gap: 0.5,
              border: '1px solid #EFEFEF',
              borderRadius: '32px',
              p: '2px',
              minHeight: 36
            }}
          >
            <Box sx={{ overflow: 'hidden' }}>{children || fallbackBreadcrumb}</Box>
            {actionContent && <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>{actionContent}</Box>}
          </Box>
        </Box>
      )}

      {isMobile && showBreadcrumbRow && (
        <Stack direction="column" gap={2} width="100%">
          <Stack direction="row" justifyContent="flex-start" sx={{ overflow: 'hidden' }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children || fallbackBreadcrumb}</Box>
          </Stack>
        </Stack>
      )}
    </AppBar>
  )
}

export default HeaderBar
